import React from "react";
import { SectionHeader } from "@/components/design-system/SectionHeader/SectionHeader";
import { FeatureCard } from "@/components/design-system/FeatureCard/FeatureCard";

import { IconAI } from "@/components/design-system/icons/IconAI";
import { IconCloudData } from "@/components/design-system/icons/IconCloudData";
import { IconConfiguration } from "@/components/design-system/icons/IconConfiguration";
import { IconPhoneProcessor } from "@/components/design-system/icons/IconPhoneProcessor";
import { IconPremiumWebsite } from "@/components/design-system/icons/IconPremiumWebsite";
import { IconAnalytics } from "@/components/design-system/icons/IconAnalytics";

const iconClass = "h-full w-full text-primary-500";

type PlatformFeature = {
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly description: string;
};

const platformFeatures: readonly PlatformFeature[] = [
  {
    icon: <IconAI className={iconClass} />,
    title: "Conversational AI Engine",
    description:
      "Help users answer real financial questions with personalized, contextual guidance at the moment it matters most.",
  },
  {
    icon: <IconCloudData className={iconClass} />,
    title: "Account Aggregation Intelligence",
    description:
      "Real-time account aggregation surfaces held-away assets, idle cash, and spending patterns — turning raw data into actionable recommendations.",
  },
  {
    icon: <IconConfiguration className={iconClass} />,
    title: "White-Labelled",
    description:
      "Your domain, your palette, your name — members and employees see only you.",
  },
  {
    icon: <IconPhoneProcessor className={iconClass} />,
    title: "Flexible Deployment",
    description:
      "Deploy Bravo seamlessly across every digital surface your users already rely on, from client portals to mobile apps to benefits platforms.",
  },
  {
    icon: <IconPremiumWebsite className={iconClass} />,
    title: "Built-In Compliance",
    description:
      "Educational guardrails, configurable disclosures, and audit-ready logging — built in from day one.",
  },
  {
    icon: <IconAnalytics className={iconClass} />,
    title: "Opportunity Signals",
    description:
      "Every conversation generates signal, continuously surfacing new opportunities for growth.",
  },
] as const;

export const PlatformSection: React.FC = () => {
  return (
    <section
      id="platform"
      className="relative w-full overflow-hidden bg-neutral-100"
    >
      <div className="section-inset pt-12 pb-12 md:py-20">
        {/* Mobile ellipse background */}
        <div
          className="pointer-events-none absolute md:hidden"
          style={{
            width: "797px",
            height: "1037px",
            left: "calc(50% + 0.5px)",
            top: "calc(50% + 172px)",
            transform: "translate(-50%,-50%)",
            zIndex: 0,
          }}
        >
          <svg width="797" height="1037" viewBox="0 0 797 1037" fill="none">
            <ellipse
              cx="398.5"
              cy="518.5"
              rx="398.5"
              ry="518.5"
              fill="url(#platform-ellipse)"
              fillOpacity="0.7"
            />
            <defs>
              <radialGradient
                id="platform-ellipse"
                cx="0"
                cy="0"
                r="1"
                gradientUnits="userSpaceOnUse"
                gradientTransform="translate(398.5 518.5) rotate(120.385) scale(602)"
              >
                <stop stopColor="#D441D6" />
                <stop offset="0.427885" stopColor="#E4A96B" />
                <stop
                  offset="0.657599"
                  stopColor="#FEB7FF"
                  stopOpacity="0.42029"
                />
                <stop offset="0.831731" stopColor="white" stopOpacity="0" />
              </radialGradient>
            </defs>
          </svg>
        </div>

        <div className="relative z-10 mb-8 text-center md:mb-14">
          <SectionHeader
            label="Platform"
            heading={
              <>
                One intelligence layer.{" "}
                <span className="text-primary-500">
                  Infinite configurations.
                </span>
              </>
            }
            description="Built for enterprise integration — Bravo is architected to work within your existing digital infrastructure so your team stays focused on growth, not implementation."
            className="md:px-20 lg:px-44"
          />
        </div>

        <div className="relative z-10 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
          {platformFeatures.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </div>
      </div>
    </section>
  );
};
